/**
 * The status vocabulary. This file is the ONLY definition of what each state is called
 * and what colour it wears — matrix cells, environment cards, drawers, plan detail and
 * counts all read it. Never inline a label or a colour for a state that lives here, and
 * never add a second file with different wording.
 *
 * Colour assignments carry two decisions worth not re-litigating:
 *
 *  - `env_only` is BLUE, outside the health ramp on purpose. It is not a degree of
 *    wrongness; it is an inbox item, something that exists and has not been decided
 *    about yet. Yellow would say "this is broken", which is usually false.
 *
 *  - `unknown` is GREY, not red. When a pull fails, every cell in that environment
 *    becomes `unknown`, and painting thousands of cells red to report one unreachable
 *    service is a lie about where the problem is. The cells dim; the red lives on the
 *    environment's column header and its Environments card, with the failure text and
 *    the time of the last successful pull.
 */

import { isActionable, SYNC_STATUSES } from 'config-hub-server/src/domain/status.js';
import type { ActionableStatus, SyncStatus as ServerSyncStatus } from 'config-hub-server/src/domain/status.js';
import type { OpResult as ServerOpResult, PlanStatus as ServerPlanStatus } from 'config-hub-server/src/domain/plan.js';
import type { AuditOutcome as ServerAuditOutcome } from 'config-hub-server/src/domain/auditTrail.js';

export type StatusGlyph =
    'check' | 'alert' | 'plus' | 'inbox' | 'off' | 'help' | 'minus' | 'x' | 'lock' | 'mail' | 'key' | 'userOff';

export type SyncStatus = ServerSyncStatus;

export interface StatusMeta {
    /** The word shown in the UI. Title Case, because it labels. */
    label: string;
    /** A Mantine palette name. Resolved scheme-aware; never a shade index. */
    color: string;
    /** Carries the meaning for anyone who cannot separate the hues. A token; `components/StatusBadge/glyphs.ts` maps it to an icon, so this module stays free of React. */
    glyph: StatusGlyph;
    /** Tooltip / aria description. One sentence, no trailing period. */
    description: string;
    /** Dimmed cells: state is not knowledge, it is the absence of it. */
    dimmed?: boolean;
}

export const SYNC_STATUS: Record<SyncStatus, StatusMeta> = {
    in_sync: {
        label: 'In sync',
        color: 'green',
        glyph: 'check',
        description: 'Desired and actual match',
    },
    drift: {
        label: 'Drift',
        color: 'yellow',
        glyph: 'alert',
        description: 'Both exist, canonical JSON differs',
    },
    missing: {
        label: 'Missing',
        color: 'red',
        glyph: 'plus',
        description: 'Targeted at this environment, but absent from it',
    },
    env_only: {
        label: 'Env only',
        color: 'blue',
        glyph: 'inbox',
        description: 'Present in this environment, absent from master',
    },
    excluded: {
        label: 'Excluded',
        color: 'gray',
        glyph: 'off',
        description: 'Not targeted at this environment',
    },
    unknown: {
        label: 'Unknown',
        color: 'gray',
        glyph: 'help',
        description: 'The last pull of this environment failed, or it was never pulled',
        dimmed: true,
    },
};

export { isActionable };
export type { ActionableStatus };

/** The statuses that mean "something to do here" — a grid's *Needs attention* toolbar count, a column header's badge, an environment card. */
export const ACTIONABLE: readonly ActionableStatus[] = SYNC_STATUSES.filter(isActionable);

/** Where **Adopt** is offered: the environment holds something master doesn't (`env_only`), or holds a different value (`drift`). */
export const isAdoptable = (status: SyncStatus): boolean => status === 'env_only' || status === 'drift';

export const syncStatusOptions = () =>
    (Object.keys(SYNC_STATUS) as SyncStatus[]).map((value) => ({
        value,
        label: SYNC_STATUS[value].label,
    }));

/** The server's own three outcomes, plus `pending` — a push not yet attempted, which never reaches the server. */
export type OpResult = ServerOpResult | 'pending';

/**
 * `conflict` is yellow, not red: nothing is broken, the world moved under the plan and
 * the operation needs re-rendering.
 */
export const OP_RESULT: Record<OpResult, StatusMeta> = {
    ok: {
        label: 'Written',
        color: 'green',
        glyph: 'check',
        description: 'Written to the environment',
    },
    conflict: {
        label: 'Conflict',
        color: 'yellow',
        glyph: 'alert',
        description: 'The record changed since the push was rendered',
    },
    failed: {
        label: 'Failed',
        color: 'red',
        glyph: 'x',
        description: 'The write was rejected',
    },
    pending: {
        label: 'Pending',
        color: 'gray',
        glyph: 'minus',
        description: 'Not attempted yet',
        dimmed: true,
    },
};

export type PlanStatus = ServerPlanStatus;

export const PLAN_STATUS: Record<PlanStatus, StatusMeta> = {
    draft: {
        label: 'Draft',
        color: 'gray',
        glyph: 'minus',
        description: 'Rendered, not yet applied',
    },
    applying: {
        label: 'Running',
        color: 'blue',
        glyph: 'minus',
        description: 'Running against the environment',
    },
    applied: {
        label: 'Pushed',
        color: 'green',
        glyph: 'check',
        description: 'Every write landed',
    },
    partial: {
        label: 'Partly pushed',
        color: 'yellow',
        glyph: 'alert',
        description: 'Some writes conflicted or failed',
    },
    failed: {
        label: 'Failed',
        color: 'red',
        glyph: 'x',
        description: 'No write landed',
    },
    cancelled: {
        label: 'Cancelled',
        color: 'gray',
        glyph: 'off',
        description: 'Abandoned before applying',
        dimmed: true,
    },
};

export type AuditOutcome = ServerAuditOutcome;

/**
 * Refused is the accent, not grey: somebody tried, and "who tried to apply to production and was
 * refused" is the row an auditor is looking for. Failed is red because something is broken.
 */
export const AUDIT_OUTCOME: Record<AuditOutcome, StatusMeta> = {
    ok: { label: 'Done', color: 'green', glyph: 'check', description: 'The mutation ran and returned' },
    refused: {
        label: 'Refused',
        color: 'rgsOrange',
        glyph: 'lock',
        description: 'Recorded before the permission check and refused by it; nothing was written',
    },
    failed: { label: 'Failed', color: 'red', glyph: 'x', description: 'The mutation ran and threw' },
    pending: { label: 'Pending', color: 'gray', glyph: 'minus', description: 'Not settled', dimmed: true },
};

/**
 * A pool user's state on the Users page — `server/src/domain/users.ts#UserStatus`, the same four
 * words. Deactivated is grey and dimmed: nothing is wrong, the account is simply out of play.
 */
export type UserStatus = 'active' | 'invited' | 'password_reset' | 'deactivated';

export const USER_STATUS: Record<UserStatus, StatusMeta> = {
    active: { label: 'Active', color: 'green', glyph: 'check', description: 'Can sign in' },
    invited: {
        label: 'Invited',
        color: 'blue',
        glyph: 'mail',
        description: 'Has a temporary password and has not signed in yet',
    },
    password_reset: {
        label: 'Password reset',
        color: 'yellow',
        glyph: 'key',
        description: 'Must set a new password at next sign-in',
    },
    deactivated: { label: 'Deactivated', color: 'gray', glyph: 'userOff', description: 'Cannot sign in', dimmed: true },
};

export const userStatusOptions = (): { value: UserStatus; label: string }[] =>
    (Object.keys(USER_STATUS) as UserStatus[]).map((value) => ({ value, label: USER_STATUS[value].label }));
